import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { KEY_ID_SUFFIX } from '../components/TrackChangesPanel/constants'
import { getContentItemViewNodeElementMapping } from '../components/TrackChangesPanel/utils/TrackChangesPanelUtils'
import { ObjectBuilder } from '../factories/ObjectBuilder'
import { ContentItemViewNodesType } from '../types/contentItemViewNode.type'
import { DragAndDropInfo } from '../types/dragDropInfo.type'
import { replaceInArray } from './workspaceEditorUtils'

/** Type representing a node in the content item view tree, potentially with children */
export type ContentViewNode = Partial<ContentItemViewNodesType> & {
    /** children nodes of content for recursion */
    children?: ContentViewNode[]
    /** id/key of content that have change */
    key?: string
}

/**
 * Finds the contentItemViewNode that contains an element with the given id.
 * Searches through elements, contentItemNodes, and nested elements.
 *
 * @param contentItemViewNodes - Array of content item view nodes to search
 * @param elementId - The id of the element to find
 * @returns The contentItemViewNode containing the element, or null if not found
 */
export const getContentItemViewNodeByElementId = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    elementId: string
): Partial<ContentItemViewNodesType> | null => {
    if (!elementId || !contentItemViewNodes) return null

    for (const node of contentItemViewNodes) {
        // Check in direct elements
        if (node.elements?.some((el) => el.id === elementId)) {
            return node
        }

        // Check in nested elements within elements
        if (node.elements) {
            for (const element of node.elements) {
                if (
                    element.elements?.some(
                        (nested: any) => nested.id === elementId
                    )
                ) {
                    return node
                }
            }
        }
    }

    return null
}

/**
 * Checks if an element with the target ID exists within a list of elements.
 * @param elements - The list of elements to search within.
 * @param targetId - The ID of the element to find.
 * @returns The element if found, null otherwise.
 */
const findElementById = (
    elements: Partial<TextContentElement>[] | undefined,
    targetId: string
): Partial<TextContentElement> | null => {
    if (!elements) return null

    for (const element of elements) {
        if (!element) continue
        if (element.id === targetId) return element
    }
    return null
}

/**
 * Transforms an array of partial TextContentElements into fully formed elements
 * using ObjectBuilder, applying default values and resolvers.
 * @param elements - The array of partial elements to transform.
 * @returns The array of transformed TextContentElements.
 */
const buildTextElements = (
    elements: Partial<TextContentElement>[]
): Partial<TextContentElement>[] => {
    return elements.map((element) =>
        new ObjectBuilder<TextContentElement>()
            .set('id', element.id)
            .set('text', element.text)
            .set('changeType', element.changeType)
            .set('elements', element.elements || [])
            .set(
                'authoredUserId',
                element.authoredUserId?.trim()
                    ? element.authoredUserId
                    : 'anonymous1'
            )
            .set('type', element.type || 'Text')
            .set('imperialUnits', element.imperialUnits)
            .set('metricUnits', element.metricUnits)
            .set('optionItemState', element.optionItemState)
            .set('placeholderText', element.placeholderText)
            .set('isPremiumPlus', element.isPremiumPlus)
            .set('isBestPractice', element.isBestPractice)
            .set('isGlobalBestPractice', element.isGlobalBestPractice)
            .build()
    )
}

/**
 * Recursively traverses a tree of ContentViewNodes, finds the node containing an element
 * with the specified ID, replaces its elements array, and returns a new tree structure.
 * Ensures immutability by creating new node objects where changes occur.
 *
 * @param nodes - The tree nodes to process.
 * @param targetElementId - The ID of the element whose parent node's elements need replacement.
 * @param replacementElements - The new array of TextContentElements to insert.
 * @param isNewElementAfterKeyedElement - Optional flag to prepend the target element to replacement elements for keyed text handling.
 * @returns The updated tree structure.
 */
export const replaceNodeElementsByElementId = (
    nodes: ContentViewNode[],
    targetElementId: string,
    replacementElements: Partial<TextContentElement>[],
    isNewElementAfterKeyedElement?: boolean
): ContentViewNode[] => {
    if (!nodes) return []

    const traverseAndReplace = (
        currentNodes: ContentViewNode[]
    ): [ContentViewNode[], boolean] => {
        let replacementOccurred = false

        const updatedNodes = currentNodes.map((node) => {
            if (replacementOccurred || !node) {
                return node
            }

            const foundTargetElement = findElementById(
                node.elements,
                targetElementId
            )
            // if current node contains the target element
            if (foundTargetElement) {
                replacementOccurred = true
                const elementsToReplace = isNewElementAfterKeyedElement
                    ? [foundTargetElement, ...replacementElements]
                    : replacementElements

                const elementsToTransform = replaceInArray(
                    node.elements,
                    targetElementId,
                    elementsToReplace
                )
                const finalElements = buildTextElements(elementsToTransform)

                return { ...node, elements: finalElements }
            }

            // if current node has children, recursively process them
            if (node.elements?.length) {
                const [updatedChildren, childReplaced] = traverseAndReplace(
                    node.elements
                )
                if (childReplaced) {
                    replacementOccurred = true
                    return { ...node, elements: updatedChildren }
                }
            }

            return node
        })

        return [updatedNodes, replacementOccurred]
    }

    const [finalTree] = traverseAndReplace(nodes)
    return finalTree
}

/**
 * Recursively traverses a tree of ContentViewNodes, finds the node with the specified key,
 * updates its comments property, and returns a new tree structure.
 * Ensures immutability by creating new node objects where changes occur.
 *
 * @param nodes - The tree nodes to process.
 * @param targetKey - The key of the node whose specifierNotes need to be updated.
 * @param updatedNotes - The new specifierNotes to set on the found node.
 * @returns The updated tree structure.
 */
export const replaceNodeNotesByKey = (
    nodes: ContentViewNode[],
    targetKey: string,
    updatedNotes: string[],
    isArticleNoteSpecifier?: boolean
): ContentViewNode[] => {
    if (!nodes) return []

    const traverseAndReplace = (
        currentNodes: ContentViewNode[]
    ): [ContentViewNode[], boolean] => {
        let replacementOccurred = false

        const updatedNodes = currentNodes.map((node) => {
            if (replacementOccurred || !node) {
                return node
            }

            // if current node has the target key
            if (node.id === targetKey) {
                replacementOccurred = true

                if (isArticleNoteSpecifier) {
                    return { ...node, parentComments: updatedNotes }
                }
                return { ...node, comments: updatedNotes }
            }

            // if current node has children, recursively process them
            if (node.children?.length) {
                const [updatedChildren, childReplaced] = traverseAndReplace(
                    node.children
                )
                if (childReplaced) {
                    replacementOccurred = true
                    return { ...node, children: updatedChildren }
                }
            }

            return node
        })

        return [updatedNodes, replacementOccurred]
    }

    const [finalTree] = traverseAndReplace(nodes)
    return finalTree
}

/**
 * Retrieves and sorts siblings of a content items based on their parent ID and order.
 * @param contentItems - The list of content items to search within.
 * @param dtvParentId - The parent ID to filter siblings by if dtvContentItemParentId is null.
 * @param dtvContentItemParentId - The parent ID to filter siblings by if not null.
 * @returns A sorted array of sibling content items.
 */
export const getSortedSiblings = (
    contentItems: Partial<ContentItemViewNodesType>[] | undefined,
    dtvParentId: string | undefined,
    dtvContentItemParentId: string | undefined
): Partial<ContentItemViewNodesType>[] => {
    const filteredItems =
        contentItems?.filter((item) => {
            if (dtvContentItemParentId) {
                return item.dtvContentItemParentId === dtvContentItemParentId
            } else {
                return (
                    item.dtvParentId === dtvParentId &&
                    !item.dtvContentItemParentId
                )
            }
        }) ?? []

    return filteredItems.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

/** Collects all descendants for multiple parents in O(n) time using index optimization. */
export const getAllDescendantsOptimized = (
    allNodes: Partial<ContentItemViewNodesType>[],
    parentIds: string[]
): Partial<ContentItemViewNodesType>[] => {
    // Build parent-child index in single pass - O(n)
    const parentChildIndex = new Map<
        string,
        Partial<ContentItemViewNodesType>[]
    >()

    allNodes.forEach((node) => {
        const parentId = node.dtvContentItemParentId
        if (parentId) {
            if (!parentChildIndex.has(parentId)) {
                parentChildIndex.set(parentId, [])
            }
            parentChildIndex.get(parentId)!.push(node)
        }
    })

    // Collect descendants using index - O(d) where d = total descendants
    const allDescendants: Partial<ContentItemViewNodesType>[] = []
    const visitedIds = new Set<string>()

    const collectDescendantsRecursive = (parentId: string) => {
        // Cycle detection
        if (visitedIds.has(parentId)) {
            return
        }
        visitedIds.add(parentId)

        const children = parentChildIndex.get(parentId) || []

        children.forEach((child) => {
            allDescendants.push(child)
            if (child.id) {
                collectDescendantsRecursive(child.id)
            }
        })
    }

    parentIds.forEach((parentId) => {
        collectDescendantsRecursive(parentId)
    })

    return allDescendants
}

/**
 * Reorders sibling contents and prepares the API payload for reordering operations.
 *
 * @param {Array} siblings - The array of sibling nodes to reorder.
 * @param {number} dragIndex - The index of the dragged node.
 * @param {number} dropIndex - The index of the drop position.
 * @param {Object} dragNode - The node being dragged.
 * @param {Object} selectedProjectElementNode - The currently selected project element node.
 * @returns {Object} An object containing the reordered siblings and the API payload.
 */
export const reorderSiblingContents = (
    siblings: any[],
    dragIndex: number,
    dropIndex: number,
    dragNode: Partial<ContentItemViewNodesType>,
    selectedProjectElementNode: ProjectElementNode | null
) => {
    const reorderedSiblings = [...siblings]
    const [draggedNode] = reorderedSiblings.splice(dragIndex, 1)

    // Insert the dragged node at the drop position.
    reorderedSiblings.splice(dropIndex, 0, draggedNode)

    // Update the order property for each sibling node.
    reorderedSiblings.forEach((node, index) => {
        node.order = index
    })

    // Prepare the API payload with the updated order.
    const apiPayload = {
        parentId: dragNode?.dtvContentItemParentId ?? dragNode?.dtvParentId,
        documentTemplateViewId: dragNode?.documentTemplateViewId ?? '',
        isContentItem:
            !!dragNode?.dtvContentItemParentId || siblings.length === 0,
        projectProductFamilyId: selectedProjectElementNode?.elementId ?? '',
        viewContentItemReorderInputs: reorderedSiblings.map((node) => ({
            contentItemId: node.id ?? '',
            order: node.order ?? 0,
        })),
    }

    return { reorderedSiblings, apiPayload }
}

/**
 *
 * @param { Partial<ContentItemViewNodesType>[]} contentItemViewNodes - The array of ContentItemViewNodes
 * @param {string[] | undefined} ids - The array of IDs to search for in the contentItemViewNodes
 * @returns {string[]} - An array of root article content item IDs
 */
export const getRootArticleContentItemIds = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    ids: string[] | undefined = undefined
): string[] => {
    if (ids?.length === 0 || !ids || !contentItemViewNodes) {
        // If no IDs are provided or contentItemViewNodes is empty, return an empty array
        return []
    }

    const contentItemViewNodeMapping = getContentItemViewNodeElementMapping(
        contentItemViewNodes,
        true
    )

    const rootArticleContentItemIds: string[] = []

    Object.keys(contentItemViewNodeMapping).forEach((id) => {
        const parts = id.split('_')
        const elementId =
            parts.at(-1) === KEY_ID_SUFFIX
                ? parts.slice(-2).join('_')
                : parts.at(-1)

        if (ids?.includes(elementId as string)) {
            const rootArticleId = findRootArticle(
                id.split('_')[0] || '',
                contentItemViewNodes
            ) // Use the contentItemId part

            if (rootArticleId) {
                rootArticleContentItemIds.push(rootArticleId)
            }
        }
    })

    // Return the unique root article content item IDs
    return Array.from(new Set(rootArticleContentItemIds))
}

/**
 * Recursively finds the root article node ID for a given node in a list of content item view nodes.
 *
 * @param nodeId - The ID of the node to start the search from.
 * @param contentItemViewNodes - The array of content item view nodes to search within.
 * @returns The `id` of the root article node if found, otherwise `undefined`.
 */
export const findRootArticle = (
    nodeId: string,
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
): string | undefined => {
    const ctvNode = contentItemViewNodes.find(
        (node) => node.id === nodeId || node.dtvItemId === nodeId
    )

    if (ctvNode) {
        // Check if the current node is an Article
        if (ctvNode.textType === 'Article') {
            return ctvNode.id
        }

        // Try finding the root article using dtvParentId
        const parentId = ctvNode.dtvParentId
        if (parentId) {
            const rootArticleId = findRootArticle(
                parentId,
                contentItemViewNodes
            )
            if (rootArticleId) {
                return rootArticleId
            }
        }

        // If not found, try finding the root article using dtvContentItemParentId
        const contentItemParentId = ctvNode.dtvContentItemParentId
        if (contentItemParentId) {
            return findRootArticle(contentItemParentId, contentItemViewNodes)
        }
    }

    return undefined
}

/**
 * Removes a text element from the content item view nodes by its ID.
 * This function traverses the tree structure and removes the specified element,
 *  ensuring that the tree remains valid and that no empty nodes are left behind.
 * @param {Array} contentItemViewNodes - The array of ContentItemViewNodes to search within.
 * @param {string} elementId - The ID of the text element to remove.
 * @returns {Array} - The updated array of ContentItemViewNodes with the specified element removed.
 *  Note: This function does not mutate the original array.
 * */
export const removeTextElementById = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    elementId: string
): Partial<ContentItemViewNodesType>[] => {
    return contentItemViewNodes
        .map(deepClean(elementId))
        .filter(
            (node): node is Partial<ContentItemViewNodesType> =>
                !!node && (node.elements ?? []).length > 0
        )
}

/**
 * Recursively cleans the tree structure by removing nodes with the specified element ID.
 * @param {string} elementId - The ID of the element to remove.
 * @returns {Function} - A function that takes a node and returns the cleaned node or null.
 */
const deepClean = (elementId: string) => <
    T extends { id?: string; elements?: T[] }
>(
    node: T
): T | null => {
    if (node.id === elementId) return null

    if (!node.elements) return { ...node }

    const cleaned = node.elements
        .map(deepClean(elementId))
        .filter((child): child is T => !!child)

    return {
        ...node,
        elements: cleaned.length > 0 ? cleaned : undefined,
    }
}

/**
 * Processes non-sibling elements in the content item view nodes based on a mutation.
 * This function identifies elements that are not siblings of the specified IDs and removes them from the content item view nodes.
 *
 * @param {Partial<ContentItemViewNodesType>[]} contentItemViewNodes - The array of content item view nodes to process.
 * @param {string[]} ids - The array of IDs to check against for sibling relationships.
 * @returns {Object} - An object containing the updated content item view nodes and the non-sibling elements.
 */
export const processNonSiblingElementsOnContentMutation = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    ids: string[]
) => {
    const elementMapping = getContentItemViewNodeElementMapping(
        contentItemViewNodes
    )
    const elements = Object.keys(elementMapping).filter((key) =>
        ids.includes(key.split('_')[1])
    )

    const nonSiblingElements: Record<string, Partial<TextContentElement>> = {}

    elements.forEach((element) => {
        const [parentId, elementId] = element.split('_')
        const siblingKeys = Object.keys(elementMapping).filter(
            (key) => key.includes(parentId) && !key.includes(elementId)
        )
        if (!siblingKeys.length) {
            nonSiblingElements[elementId] = elementMapping[element]
        }
    })

    let updatedContentItem = [...contentItemViewNodes]
    Object.entries(nonSiblingElements).forEach(([key, textElement]) => {
        updatedContentItem = [
            ...removeTextElementById(
                updatedContentItem,
                textElement.id as string
            ),
        ]
    })

    return {
        updatedContentItem,
        nonSiblingElements,
    }
}

/**
 * Updates the `originalNodes` array by merging it with the `replacementNodes`.
 *
 * This function ensures that the nodes in `replacementNodes` replace their corresponding
 * nodes in the `originalNodes` array, while preserving the rest of the nodes.
 *
 * @param {Partial<ContentItemViewNodesType>[]} originalNodes - The original array of content item view nodes.
 * This array represents the full tree structure before the update.
 *
 * @param {Partial<ContentItemViewNodesType>[]} replacementNodes - The array of replacement nodes.
 * These nodes contain updated order or other properties.
 *
 * @returns {Partial<ContentItemViewNodesType>[]} - A new array of content item view nodes where the nodes
 * in `replacementNodes` have replaced their corresponding nodes in `originalNodes`.
 */
export const replaceNodesById = (
    originalNodes: Partial<ContentItemViewNodesType>[],
    replacementNodes: Partial<ContentItemViewNodesType>[]
) => {
    return originalNodes.map(
        (node) =>
            replacementNodes.find((sibling) => sibling.id === node.id) || node
    )
}

/**
 * Finds the root article node for a given content item node ID by traversing up the tree.
 * This function recursively searches upward through parent relationships to find
 * the nearest Article-type ancestor.
 *
 * @param contentItemViewNodes - Array of content item view nodes representing the entire tree
 * @param nodeId - The ID of the node to find the root article for
 * @param visitedIds - Optional set of already visited node IDs to prevent infinite loops (used internally)
 * @returns The root article node, or undefined if none found
 */
export const findRootArticleNode = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    nodeId: string,
    visitedIds: Set<string> = new Set()
): Partial<ContentItemViewNodesType> | undefined => {
    // prevent infinite recursion by tracking visited nodes
    if (visitedIds.has(nodeId)) return undefined
    visitedIds.add(nodeId)

    // find the current node
    const currentNode = contentItemViewNodes.find((node) => node.id === nodeId)
    if (!currentNode) return undefined

    // check if current node is an article
    if (currentNode.textType === 'Article') {
        return currentNode
    }

    // find parent ID through different potential relationships
    // check for direct parent ID
    if (currentNode.dtvContentItemParentId) {
        return findRootArticleNode(
            contentItemViewNodes,
            currentNode.dtvContentItemParentId,
            visitedIds
        )
    }

    // check for indirect parent ID via refBodItemId
    if (currentNode.refBodItemId) {
        const parentNode = contentItemViewNodes.find(
            (node) => node.dtvItemId === currentNode.refBodItemId
        )
        if (parentNode?.id) {
            return findRootArticleNode(
                contentItemViewNodes,
                parentNode.id,
                visitedIds
            )
        }
    }

    // check for parent via dtvParentId
    if (currentNode.dtvParentId) {
        const parentNode = contentItemViewNodes.find(
            (node) => node.dtvItemId === currentNode.dtvParentId
        )
        if (parentNode?.id) {
            return findRootArticleNode(
                contentItemViewNodes,
                parentNode.id,
                visitedIds
            )
        }
    }

    return undefined
}

/**
 * Retrieves the drag and drop nodes based on the provided drag and drop information.
 *
 * @param {DragAndDropInfo} info - The drag and drop information containing the source and target nodes.
 * @param {Partial<ContentItemViewNodesType>[]} contentItemViewNodes - The array of content item view nodes.
 * @returns {Object} An object containing the drag key, drop key, drag node, drop node, and event.
 */
export const getDragAndDropNodes = (
    info: DragAndDropInfo,
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
) => {
    const dragKey = info.dragNode?.key
    const dropKey = info.node?.key

    const dragNode = contentItemViewNodes.find((node) => node.id === dragKey)
    const dropNode = contentItemViewNodes.find((node) => node.id === dropKey)
    const event = info.event

    return { dragKey, dropKey, dragNode, dropNode, event }
}

/**
 * Determines the content owner type for a given node within a parent node.
 *
 * @param parentNode The parent content item node
 * @param node The content item node to check
 * @returns The owner type as a string, or undefined if not found.
 */
export const getContentOwnerType = (
    parentNode: Partial<ContentItemViewNodesType> | null | undefined,
    node: Partial<ContentItemViewNodesType>
): string => {
    let contentItemOwnerType: string | undefined
    const isPTP = parentNode?.type === 'ProductTypeProperty'

    if (parentNode?.elements) {
        parentNode.elements.forEach((element: any) => {
            if (element?.items) {
                const contentItem = element.items.find(
                    (item: any) => item?.id === node?.dtvContentItemParentId
                )
                if (contentItem && !contentItem.parentId) {
                    contentItemOwnerType = isPTP ? node?.type : parentNode.type
                }
            }
        })
    }

    return contentItemOwnerType ?? 'Nugget'
}

/**
 * Recursively builds a parent key string representing the hierarchy of parent nodes for a given node.
 *
 * @param contentItemViewNodes - The array of content item view nodes to search within.
 * @param node - The node for which to build the parent key chain.
 * @returns A string representing the concatenated keys of all parent nodes, separated by '|', always ending with '|'.
 * Example return value: "Summary|Section Includes|"
 */
export const getParentKeyChain = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    node: Partial<ContentItemViewNodesType>
): string => {
    const parentNode = contentItemViewNodes?.find((item) =>
        node?.dtvContentItemParentId
            ? item?.id === node?.dtvContentItemParentId
            : item?.dtvItemId === node?.dtvParentId
    )

    if (!parentNode || parentNode.partType == 'Part') {
        return ''
    }

    let parentKey = parentNode.elements?.[0]?.text ?? ''
    parentKey = parentKey.replace(/:/g, '').trim()

    const parentKeyChain = getParentKeyChain(contentItemViewNodes, parentNode)

    if (parentKeyChain && parentKey) {
        return `${parentKeyChain}|${parentKey}|`
    }
    if (parentKey) {
        return `${parentKey}|`
    }
    return parentKeyChain
}

/**
 * Checks if an API response's element ID (selected family Id) matches the currently active element.
 * Prevents race conditions where stale responses update the wrong section's content.
 *
 * @param response - The API response (GetProjectProductFamilyViewResponse or GetProjectNuggetContentViewResponse)
 * @param currentElementId - The currently active element ID from the store
 * @returns true if the response belongs to the current element, false if stale
 */
export const isStaleResponse = (
    response:
        | { getProjectProductFamilyView?: { id?: string } }
        | { getProjectNugget?: { id?: string } },
    currentElementId: string | undefined
): boolean => {
    // Extract elementId from response based on response type
    const responseElementId =
        'getProjectProductFamilyView' in response
            ? response.getProjectProductFamilyView?.id
            : 'getProjectNugget' in response
            ? response.getProjectNugget?.id
            : undefined

    // If either ID is missing, assume response is valid
    if (!responseElementId || !currentElementId) {
        return false
    }

    // Response is stale if IDs don't match
    return responseElementId !== currentElementId
}

/**
 * Validates if a mutation/operation is still valid by comparing initial vs current activeTab.
 * Prevents race conditions where tab switches during async operations cause content corruption.
 *
 * @param initialActiveTab - The activeTab when the operation started (can be null)
 * @param currentActiveTab - The current activeTab from store
 * @returns true if tabs match or initial is null (valid), false if tabs differ (stale)
 */
export const isActiveTabConsistent = (
    initialActiveTab: number | null,
    currentActiveTab: number
): boolean => {
    // If initialActiveTab wasn't captured, assume operation is valid
    if (initialActiveTab === null) {
        return true
    }

    return initialActiveTab === currentActiveTab
}
